<template>
  <div>
    <div class="row">
      <div class="col-6 d-flex align-items-center justify-content-lg-start">
        <Button
          v-if="showBackButton && actionType === 'save' || 'add'"
          :label="$t('List page')"
          size="md"
          icon="ArrowLeftIcon"
          show-icon
          class="mb-1"
          @click="backNavigationAction"
        />
      </div>
      <div class="col-6 d-flex justify-content-end">
        <Button
          v-if="showSearchButton"
          :label="$t('Search')"
          size="md"
          icon="SearchIcon"
          show-icon
          @click="showSearchField"
        />
      </div>
    </div>
    <b-card v-if="!showSearchButton">
      <b-row class="position-relative">
        <b-col md="4">
          <validation-observer ref="searchRules">
            <b-form @submit.prevent="handleSearch">
              <b-row>
                <Alert
                  :show="showFormErrorAlert"
                  :message="$t('Customer\'s account name or account number or customer id is required')"
                />

                <b-col md="12">
                  <InputField
                    id="account-number"
                    v-model="searchParams.accountNumber"
                    :label="$t('Account Number')"
                    :placeholder="$t('Enter account number')"
                    type="text"
                  />
                </b-col>

                <b-col md="12">
                  <InputField
                    id="account-name"
                    v-model="searchParams.accountName"
                    :label="$t('Account Name')"
                    :placeholder="$t('Enter account name')"
                    type="text"
                  />
                </b-col>
                <b-col md="12">
                  <InputField
                    id="customer-id"
                    v-model="searchParams.customerID"
                    :label="$t('Customer ID')"
                    :placeholder="$t('Enter customer Id')"
                    type="text"
                  />
                </b-col>
                <b-col md="12">
                  <InputField
                    id="reference-number"
                    v-model="searchParams.referenceNumber"
                    :label="$t('Reference Number')"
                    :placeholder="$t('Enter reference number')"
                    type="text"
                  />
                </b-col>

                <b-col md="12">
                  <Button
                    :label="$t('Search')"
                    size="md"
                    class="mt-2"
                    block
                    @click="handleSearch"
                  />
                </b-col>
              </b-row>
            </b-form>
          </validation-observer>
        </b-col>
      </b-row>
    </b-card>

    <form-wizard
      v-if="showSearchButton"
      ref="outwardTransferForm"
      :title="null"
      :subtitle="null"
      layout="vertical"
      :back-button-text="$t('Previous')"
      :next-button-text="$t('Next')"
      class="wizard-vertical mb-3"
    >

      <!-- Sender Account Details -->
      <tab-content
        :title="$t('Account Details')"
        :before-change="validateAccountDetails"
      >
        <validation-observer ref="accountDetailsRule">
          <b-form @submit.prevent="handleSubmit">
            <b-row>
              <b-col
                cols="12"
                class="mb-3 mt-2"
              >
                <h5 class="section-label mb-0 mt-0">
                  {{ $t('Sender Account Details') }}
                </h5>
              </b-col>
              <b-col md="4">
                <InputField
                  id="account-name"
                  v-model="customer.accountName"
                  :label="$t('Account Name')"
                  type="text"
                  readonly
                />
              </b-col>

              <b-col md="4">
                <InputField
                  id="account-number"
                  v-model="customer.accountNumber"
                  :label="$t('Account Number')"
                  type="text"
                  readonly
                />
              </b-col>

              <b-col md="4">
                <InputField
                  id="branch"
                  v-model="customer.branchName"
                  :label="$t('Branch')"
                  type="text"
                  readonly
                />
              </b-col>

              <b-col md="4">
                <InputField
                  id="account-status"
                  v-model="customer.accountStatus"
                  :label="$t('Account Status')"
                  type="text"
                  readonly
                />
              </b-col>
              <b-col md="4">
                <InputField
                  id="available-balance"
                  v-model="customer.availableBalance"
                  :label="$t('Available Balance')"
                  type="text"
                  readonly
                />
              </b-col>
              <b-col md="4">
                <InputField
                  id="withdrawable-balance"
                  v-model="customer.withdrawableBalance"
                  :label="$t('Withdrawable Balance')"
                  type="text"
                  readonly
                />
              </b-col>
            </b-row>
            <b-row>
              <b-col
                cols="12"
                class="mb-2"
              >
                <h5 class="text-capitalize mb-2 mt-3">
                  {{ $t('Transfer Details') }}
                </h5>
              </b-col>
              <b-col md="4">
                <InputNumberField
                  id="amount"
                  v-model="outwardTransferFormData.amount"
                  :label="$t('Amount')"
                  :placeholder="$t('Enter amount')"
                  :rules="`required|positive`"
                  :error-name="$t('Amount')"
                  @input="checkAmountSelected"
                />
              </b-col>
              <b-col md="4">
                <SelectField
                  id="beneficiary-bank"
                  v-model="outwardTransferFormData.beneficiaryBank"
                  :label="$t('Beneficiary Bank')"
                  :options="bankListOptions"
                  :error-name="$t('Beneficiary Bank')"
                  rules="required"
                  item="label"
                  :reduce="option => option"
                  :placeholder="$t('Select')"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col md="4">
                <InputField
                  id="beneficiary-bank-name"
                  v-model="outwardTransferFormData.beneficiaryAccountNumber"
                  :label="$t('Beneficiary Account Number')"
                  :placeholder="$t('Enter account number')"
                  :error-name="$t('Beneficiary Account Number')"
                  rules="required|positive"
                  type="number"
                  :formatter="checkNumberLimit"
                  @input="setBeneficiaryBankName"
                />
              </b-col>

              <b-col md="4">
                <InputField
                  id="beneficiary-name"
                  v-model="outwardTransferFormData.beneficiaryName"
                  :label="$t('Beneficiary Name')"
                  :placeholder="$t('Enter beneficiary name')"
                  type="text"
                  rules="required"
                  :error-name="$t('Beneficiary Name')"
                  readonly
                >
                  />
                </inputfield></b-col>

              <b-col md="8">
                <InputField
                  id="narration"
                  v-model="outwardTransferFormData.narration"
                  :label="$t('Narration')"
                  :placeholder="$t('Enter narration')"
                  type="text"
                  rules="required"
                  error-name="narration"
                />
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </tab-content>

      <!-- Transaction Summary -->
      <tab-content
        :title="$t('Transaction Summary')"
        :before-change="validateFees"
      >
        <validation-observer ref="feesRule">
          <b-form>
            <b-row>
              <b-col
                cols="12"
                class="mb-0"
              >
                <h5 class="section-label mb-0">
                  {{ $t('Transaction Summary') }}
                </h5>
              </b-col>
            </b-row>
            <b-row
              class="mt-3 mb-5"
            >
              <b-col md="8">
                <table
                  align="center"
                >
                  <tbody>
                    <tr>
                      <td
                        rowspan="1"
                        colspan="2"
                      ><h5
                        class="section-label mb-0 mt-0"
                        style="padding: 10px; text-align: left; color: #000;"
                      >
                        {{ $t('Sender Account Details') }}
                      </h5>
                      </td></tr>
                    <tr>
                      <td>
                        <p style="font-size: 14px; color: #aaa; margin: 10px;">
                          {{ $t('Account Number') }}
                        </p></td>
                      <td><p style="font-size: 14px; font-weight: 600; margin: 10px;">
                        {{ customer.accountNumber }}
                      </p></td>
                    </tr>
                    <tr>
                      <td><p style="font-size: 14px; color: #aaa; margin: 10px;">
                        {{ $t('Account Name') }}
                      </p></td>
                      <td><p style="font-size: 14px; font-weight: 600; margin: 10px;">
                        {{ customer.accountName }}
                      </p></td>
                    </tr>
                    <tr>
                      <td
                        rowspan="1"
                        colspan="2"
                      ><h5
                        class="section-label mb-0 mt-2"
                        style="padding: 10px; text-align: left; color: #000;"
                      >
                        {{ $t('Beneficiary Information') }}
                      </h5>
                      </td></tr>
                    <tr>
                      <td>
                        <p style="font-size: 14px; color: #aaa; margin: 10px;">
                          {{ $t('Account Number') }}
                        </p></td>
                      <td><p style="font-size: 14px; font-weight: 600; margin: 10px;">
                        {{ outwardTransferFormData.beneficiaryAccountNumber }}
                      </p></td>
                    </tr>
                    <tr>
                      <td><p style="font-size: 14px; color: #aaa; margin: 10px;">
                        {{ $t('Account Name') }}
                      </p></td>
                      <td><p style="font-size: 14px; font-weight: 600; margin: 10px;">
                        {{ outwardTransferFormData.beneficiaryName }}
                      </p></td>
                    </tr>
                    <tr>
                      <td><p style="font-size: 14px; color: #aaa; margin: 10px;">
                        {{ $t('Bank') }}
                      </p></td>
                      <td><p style="font-size: 14px; font-weight: 600; margin: 10px;">
                        {{ outwardTransferFormData.beneficiaryBank.label }}
                      </p></td>
                    </tr>
                    <tr>
                      <td><p style="font-size: 14px; color: #aaa; margin: 10px;">
                        {{ $t('Transaction Description') }}
                      </p></td>
                      <td><p style="font-size: 14px; font-weight: 600; margin: 10px;">
                        {{ outwardTransferFormData.narration }}
                      </p></td>
                    </tr>
                    <tr>
                      <td><p style="font-size: 14px; color: #aaa; margin: 10px;">
                        {{ $t('Request Reference') }}
                      </p></td>
                      <td><p style="font-size: 14px; font-weight: 600; margin: 10px;">
                        {{ bankNameDetails.transactionRefernce }}
                      </p></td>
                    </tr>
                    <tr>
                      <td
                        rowspan="1"
                        colspan="2"
                      ><h5
                        class="section-label mb-0 mt-2"
                        style="padding: 10px; text-align: left; color: #000;"
                      >
                        {{ $t('Amount Summary') }}
                      </h5>
                      </td></tr>
                    <tr>
                      <td><p style="font-size: 14px; color: #aaa; margin: 10px;">
                        {{ $t('Fee') }}
                      </p></td>
                      <td><p style="font-size: 14px; font-weight: 600; margin: 10px;">
                        {{ toBalance(outwardFeeData.fee) }}
                      </p></td>
                    </tr>
                    <tr>
                      <td><p style="font-size: 14px; color: #aaa; margin: 10px;">
                        {{ $t('Amount') }}
                      </p></td>
                      <td><p style="font-size: 14px; font-weight: 600; margin: 10px;">
                        {{ toBalance(Number(outwardTransferFormData.amount * 100).toFixed()) }}
                      </p></td>
                    </tr>
                    <tr>
                      <td><p style="font-size: 14px; color: #aaa; margin: 10px;">
                        {{ $t('Total Amount') }}
                      </p></td>
                      <td><p style="font-size: 14px; font-weight: 600; margin: 10px;">
                        {{ toBalance(outwardFeeData.formattedTotalAmount) }}
                      </p></td>
                    </tr>
                  </tbody>
                </table>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </tab-content>

      <template
        slot="finish"
      >
        <div class="wizard-footer-right">
          <Button
            :label="$t('Continue')"
            :loading="isLoading"
            :disabled="isDisabled"
            size="lg"
            @click="onViewSignatoryMandate"
          />
        </div>
      </template>

      <FullScreenSpinner :show="showSpinner" />
    </form-wizard>

    <b-modal
      id="signatory-mandate-table"
      ref="signatoryMandateTable"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-title="$t('Submit')"
      centered
      size="lg"
      :ok-disabled="hasSelectedSignatory"
      :title="$t('Signatory Mandate')"
      @ok="handleSubmit"
    >
      <b-tabs>
        <b-tab
          :title="$t('Account Signatories')"
          active
        >

          <b-table
            ref="signatoryTable"
            select-mode="single"
            :items="signatoryList"
            responsive
            :fields="translatedSignatoryListHeader"
            :per-page="perPage"
            :current-page="currentPage"
            primary-key="id"
            show-empty
            :empty-text="$t('No result found')"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            class="position-relative mt-2"
          >
            <template #cell(SN)="data">
              {{ getOverallIndex(data.index) }}
            </template>

            <template #cell(select)="data">
              <b-form-group class="mt-1">
                <b-form-checkbox
                  v-model="data.item.select"
                  @change="onSelect"
                />
              </b-form-group>
            </template>

            <template #cell(passport)="data">
              <b-media vertical-align="center">
                <template #aside>
                  <b-avatar
                    size="32"
                    :src="data.item.passport"
                    button
                    @click="onViewPassport(data.item.passport, data.item.fullName)"
                  />
                </template>
              </b-media>
            </template>

            <template #cell(signature)="data">
              <b-media vertical-align="center">
                <template #aside>
                  <b-avatar
                    size="32"
                    :src="data.item.signature"
                    button
                    @click="onViewSignature(data.item.signature, data.item.fullName)"
                  />
                </template>
              </b-media>
            </template>
          </b-table>

        </b-tab>

        <b-tab
          :title="$t('Mandate Information')"
        >
          <b-table
            ref="mandateListTable"
            select-mode="single"
            :items="mandateList"
            responsive
            :fields="translatedMandateListHeaders"
            primary-key="id"
            selectable
            show-empty
            :empty-text="$t('No data available')"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            class="position-relative"
          >
            <template #cell(SN)="data">
              {{ getOverallIndex(data.index) }}
            </template>
            <template #cell(isLimited)="data">
              {{ data.value === false ? $t('Unlimited') : $t('Limited') }}
            </template>
            <template #cell(amount)="data">
              {{ data.value === 0 ? 'Unlimited' : toBalance(data.value) }}
            </template>
          </b-table>

        </b-tab>
      </b-tabs>
    </b-modal>

    <b-modal
      id="avatar-modal"
      ref="avatarModal"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="avatarModalTitle"
      @hidden="resetAvatarModal"
      @cancel="resetAvatarModal"
    >

      <div class="m-2 text-center">

        <b-row>
          <b-col cols="12">
            <b-img
              v-if="avatarLg"
              :src="avatarLg"
              :alt="avatarModalTitle"
              fluid
            />
            <b-avatar
              v-else
              size="30rem"
              :src="avatarLg"
            />
          </b-col>

        </b-row>

      </div>

      <template
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>

    <b-modal
      id="search-modal"
      ref="search-modal-lg"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Customer Details')"
      cancel-title-only
      @hidden="resetModal"
    >

      <b-table
        ref="customerListTable"
        select-mode="single"
        :items="customerList"
        responsive
        :fields="translatedTableHeaders"
        primary-key="id"
        selectable
        show-empty
        :empty-text="$t('No result found')"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        class="position-relative"
        @row-selected="onSelectCustomer"
      >

        <template #cell(SN)="data">
          {{ getOverallIndex(data.index) }}
        </template>

      </b-table>

      <TablePagination
        v-model="currentPage"
        :from="from"
        :to="to"
        :total-rows="totalEntries"
        :per-page="perPage"
      />

      <template
        #modal-footer="{ }"
      >
        <div />
      </template>

    </b-modal>

    <b-modal
      id="memo-info-modal"
      ref="memoInfoModal"
      :ok-title="$t('Cancel')"
      ok-only
      :title="$t('Memo')"
      ok-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
    >
      <b-table
        ref="memoListTable"
        select-mode="single"
        :items="memoGetData"
        responsive
        :fields="translatedMemoListHeaders"
        primary-key="id"
        selectable
        show-empty
        :empty-text="$t('No data available')"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        class="position-relative"
      >
        <template #cell(SN)="data">
          {{ getOverallIndex(data.index) }}
        </template>
      </b-table>
    </b-modal>

    <FullScreenSpinner :show="showSpinner" />
  </div>
</template>

<script>
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import {
  BRow,
  BCol,
  BForm,
  BCard,
  VBTooltip,
  BTable,
  BMedia, BAvatar, BFormGroup, BFormCheckbox, BTabs, BTab, BImg,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { mapActions, mapGetters } from 'vuex'
import { ValidationObserver } from 'vee-validate'
import { required, maxValue, positive } from '@validations'
import { toastAlert } from '@core/mixins/toast'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { v4 as uuidv4 } from 'uuid'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import TablePagination from '@/views/components/table/TablePagination.vue'
import {
  toAmount, toBalance, formatAmount, toKobo,
} from '@/helpers/formatAmount'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import InputNumberField from '../../../../forms/InputNumberField.vue'
import Button from '../../../../components/button/Button.vue'
import Alert from '../../../../components/alert/Alert.vue'
import { transformMandateRule } from '@/helpers/transformers'
import { eChannelsPermission } from '../../../../../helpers/permissions'

export default {
  components: {
    FormWizard,
    TabContent,
    BCard,
    BRow,
    BForm,
    BCol,
    Button,
    BTable,
    FullScreenSpinner,
    InputField,
    SelectField,
    ValidationObserver,
    Alert,
    TablePagination,
    InputNumberField,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    BMedia,
    BAvatar,
    BFormGroup,
    BFormCheckbox,
    BTabs,
    BTab,
    BImg,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
    'b-tooltip': VBTooltip,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, toastAlert],
  data() {
    return {
      customerType: '',
      required,
      toAmount,
      maxValue,
      toBalance,
      positive,
      isDisabled: false,
      isCustomDisabled: false,
      isVisible: '',
      showEditable: '',
      isLoading: false,
      isEditable: false,
      isReadonly: true,
      showSpinner: false,
      showSearchButton: false,
      showBackButton: false,
      showCustomerInfo: false,
      showFormErrorAlert: false,
      sortBy: 'accountName',
      customerList: [],
      actionType: 'add',
      searchParams: {
        accountNumber: null,
        accountName: null,
        customerID: null,
        referenceNumber: null,
      },
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'customerID', sortable: true, label: 'Customer ID' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
      ],
      customer: {
        accountName: '',
        accountNumber: '',
        branchName: '',
        accountStatus: '',
        availableBalance: '',
        withdrawableBalance: '',
        // unformattedWithdrawableBalance: '',
      },
      outwardTransferFormData: {
        beneficiaryBank: '',
        beneficiaryAccountNumber: '',
        beneficiaryName: '',
        amount: '',
        narration: '',
      },
      outwardFeeData: {
        fee: '',
        totalAmount: '',
        formattedTotalAmount: '',
      },
      bankNameDetails: {},
      permission: '',

      sortDesc: false,
      avatarLg: '',
      avatarModalTitle: '',
      selectedSignatories: [],
      selected: [],
      signatoryList: [],
      signatoryMandateList: [],
      mandateList: [],
      perPage: 10,
      currentPage: 1,
      mandateListHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'rule', sortable: true, label: 'Rule' },
        { key: 'isLimited', sortable: true, label: 'limit' },
        { key: 'amount', sortable: true, label: 'Amount' },
      ],
      signatoryListHeader: [
        { key: 'SN', label: 'S/N' },
        { key: 'select', label: 'Select' },
        { key: 'fullName', sortable: true, label: 'Full Name' },
        { key: 'category', sortable: true, label: 'Category' },
        { key: 'passport', sortable: true, label: 'Passport' },
        { key: 'signature', sortable: true, label: 'Signature' },
      ],
      memoListHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', label: 'Account Number' },
        { key: 'actionType', label: 'Action Type' },
        { key: 'details', label: 'Reason' },
        { key: 'comments', label: 'Comments' },
        { key: 'initiatedBy', label: 'Initiated By' },
      ],
      memoGetData: [],
    }
  },
  computed: {
    translatedMandateListHeaders() {
      return this.mandateListHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedSignatoryListHeader() {
      return this.signatoryListHeader.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedMemoListHeaders() {
      return this.memoListHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    totalEntries() {
      return this.customerList.length
    },
    numberOfPages() {
      return Math.floor((this.totalEntries + this.perPage - 1) / this.perPage)
    },
    from() {
      return (this.currentPage * this.perPage) - (this.perPage - 1)
    },
    to() {
      return Math.min(this.from + this.perPage - 1, this.totalEntries)
    },
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
      bankListOptions: 'eChannels/allBankOptions',
    }),
    hasSelectedSignatory() {
      return !this.selectedSignatories.length
    },
  },
  mounted() {
    const { type } = this.$route.params
    this.actionType = type
  },
  methods: {
    ...mapActions({
      searchCustomerAccount: 'account/searchCustomerAccountAction',
      getUserBalance: 'account/getCASABalanceAction',
      postOutwardTransfer: 'eChannels/postOutwardTransferAction',
      getBankNameByAccountNumber: 'eChannels/getBankNameByAccountNumber',
      getFeeRange: 'eChannels/getFeeRangeAction',
      getAccountSignatories: 'account/getAccountSignatoriesAction',
      getByAccountNumber: 'account/getByAccountNumber',
    }),
    onViewSignatoryMandate() {
      this.$root.$emit('bv::show::modal', 'signatory-mandate-table')
    },
    onSelect(value) {
      if (value) {
        this.selectedSignatories.push(value)
      } else {
        this.selectedSignatories.pop()
      }
    },
    async fetchAccountSignatories({ accountNumber }) {
      try {
        const response = await this.getAccountSignatories({ accountNumber, permission: 'INITIATE_TRANSFER' })

        if (response.status === 200 && response.data.data) {
          this.signatoryList = response.data.data.map(item => ({
            fullName: item.name,
            passport: item.passportFileUrl,
            signature: item.signatureFileUrl,
            category: item.category,
            select: false,
          }))
        } else {
          this.signatoryList = []
          this.signatoryMandateList = []
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    onViewPassport(image, name) {
      this.avatarLg = image
      this.avatarModalTitle = `${name}'s Passport`
      this.$root.$emit('bv::show::modal', 'avatar-modal')
    },
    onViewSignature(image, name) {
      this.avatarLg = image
      this.avatarModalTitle = `${name}'s Signature`
      this.$root.$emit('bv::show::modal', 'avatar-modal')
    },
    resetAvatarModal() {
      this.avatarLg = ''
      this.avatarModalTitle = ''
    },
    async handleSearch() {
      try {
        const isSearchParamsNotEmpty = Object.values(this.searchParams).every(params => (params === null || params === ''))
        if (!isSearchParamsNotEmpty) {
          this.showFormErrorAlert = false
          this.showSpinner = true
          const response = await this.searchCustomerAccount({
            search: this.searchParams,
            page: this.currentPage,
            limit: this.perPage,
            permission: 'INITIATE_TRANSFER',
          })
          const { status, data } = response
          if (status === 200 && data?.data) {
            this.customerList = data.data
            this.totalCount = data.recordCount
            this.showSpinner = false
            this.$root.$emit('bv::show::modal', 'search-modal')
          } else {
            this.customerList = []
            this.totalCount = data.recordCount
            this.$root.$emit('bv::show::modal', 'search-modal')
          }
        } else {
          this.showFormErrorAlert = true
          this.showSpinner = false
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
    checkNumberLimit(e) {
      return String(e).substring(0, 10)
    },
    checkAmountSelected(value) {
      if (value) {
        this.isReadonly = false
        return
      }
      this.isReadonly = true
      this.outwardTransferFormData.beneficiaryBank = ''
      this.outwardTransferFormData.beneficiaryAccountNumber = ''
      this.outwardTransferFormData.beneficiaryName = ''
    },
    async setBeneficiaryBankName() {
      try {
        this.showSpinner = true
        if (this.outwardTransferFormData.beneficiaryBank.value && this.outwardTransferFormData.beneficiaryAccountNumber.length === 10) {
          this.isBusy = true
          const formData = {
            destinationInstitutionCode: this.outwardTransferFormData.beneficiaryBank.value,
            accountNumber: this.outwardTransferFormData.beneficiaryAccountNumber,
            sourceAccountNumber: this.customer.accountNumber,
            sourceAccountName: this.customer.accountName,
            amount: toKobo(this.outwardTransferFormData.amount),
            channelCode: '2',
            clientRef: uuidv4(),
          }
          const response = await this.getBankNameByAccountNumber({
            formData, permission: eChannelsPermission.INITIATE_TRANSFER,
          })
          const { data } = response
          if (data.data !== null) {
            this.isBusy = false
            this.outwardTransferFormData.beneficiaryName = data.accountName
            this.bankNameDetails = {
              ...data,
            }
            this.showSpinner = false
          }
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
        this.showSpinner = false
      }
    },
    async fetchFeeRange() {
      try {
        this.isBusy = true
        this.showSpinner = true
        this.isDisabled = false
        const response = await this.getFeeRange({
          amount: Number(toKobo(this.outwardTransferFormData.amount)).toFixed(),
        })
        const { data } = response
        if (data !== null) {
          this.isBusy = false
          this.outwardFeeData.fee = data.fee
          this.outwardFeeData.formattedTotalAmount = (data.fee + toKobo(this.outwardTransferFormData.amount)).toFixed()
          this.outwardFeeData.totalAmount = (this.outwardTransferFormData.amount + formatAmount(data.fee))
          this.showSpinner = false
        }
      } catch (error) {
        this.handleError(error)
        this.isDisabled = true
      } finally {
        this.showSpinner = false
        this.isBusy = false
      }
    },
    async onSelectCustomer(items) {
      try {
        const [customer] = items
        this.hideModal('search-modal')
        this.showSpinner = true
        const response = await this.getUserBalance({ accountNumber: customer.accountNumber, permission: 'INITIATE_TRANSFER' })
        const { data, status } = response
        if (status === 200) {
          this.customer = {
            ...customer,
            availableBalance: toBalance(data.availableBalance),
            withdrawableBalance: toBalance(data.withdrawableBalance),
            // unformattedWithdrawableBalance: formatAmount(data.withdrawableBalance),
          }
          if (data.memos !== null) {
            this.memoGetData = data.memos
            // Memo info modal intentionally not shown to avoid interrupting outward transfer flow
            // this.$root.$emit('bv::show::modal', 'memo-info-modal')
          }
          await this.fetchAccountSignatories({ accountNumber: customer.accountNumber })
          const accountResponse = await this.getByAccountNumber({ accountNumber: customer.accountNumber, permission: 'INITIATE_TRANSFER' })
          if (accountResponse.status === 200 && accountResponse.data.data) {
            this.mandateList = transformMandateRule(accountResponse.data.data.mandateRuleIndex)
          }
        }
        this.showCustomerInfo = true
        this.showSearchButton = true
        this.showBackButton = true
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
    async handleSubmit() {
      this.$refs.accountDetailsRule.validate().then(success => {
        if (success) {
          const text1 = this.$t('Are you sure you want to transfer the sum of')
          const text2 = this.$t('to')
          const text3 = this.$t('in')
          this.confirm(this.outwardTransferAction, { text: `${text1} ${toAmount(this.outwardTransferFormData.amount)} ${text2} ${this.outwardTransferFormData.beneficiaryName} ${text3} ${this.outwardTransferFormData.beneficiaryBank.label}?` })
        }
      })
    },
    async outwardTransferAction() {
      try {
        this.isLoading = true
        this.isDisabled = true
        const postingApprovalModel = {
          amount: Number(toKobo(this.outwardTransferFormData.amount)).toFixed(),
          initiatedBy: '',
          approvedBy: '',
          postingType: 'ElectronicChannelsPosting',
        }
        const formData = {
          postingApprovalModel,
          beneficiaryAccountName: this.outwardTransferFormData.beneficiaryName,
          beneficiaryAccountNumber: this.outwardTransferFormData.beneficiaryAccountNumber,
          beneficiaryBankName: this.outwardTransferFormData.beneficiaryBank.label,
          originatorNarration: this.outwardTransferFormData.narration,
          beneficiaryNarration: this.outwardTransferFormData.narration,
          amount: Number(toKobo(this.outwardTransferFormData.amount)).toFixed(),
          originatorAccountNumber: this.customer.accountNumber,
          originatorAccountName: this.customer.accountName,
          channelCode: this.bankNameDetails.channelCode,
          beneficiaryBankVerificationNumber: this.bankNameDetails.bankVerificationNumber,
          destinationInstitutionCode: this.bankNameDetails.destinationInstitutionCode,
          clientRef: this.bankNameDetails.clientRef,
          transactionReference: this.bankNameDetails.transactionReference,
          transactionChannel: '7',
          initiatedBy: this.userDetails.email,
        }
        const response = await this.postOutwardTransfer(formData)
        const { status, data } = response
        if (status === 200 && data) {
          this.showCustomerInfo = false
          this.isLoading = false
          this.isDisabled = false
          this.resetInputField()
          this.resetSearchParams()
          this.alertSuccess({ text: this.$t('Your Outward Transfer request was successful') })
          this.$router.push('outward-transfers')
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.isDisabled = false
      }
    },
    resetInputField() {
      this.searchParams = { ...null }
      this.outwardTransferFormData = { ...null }
      this.customer = { ...null }
      this.isLoading = false
      this.isDisabled = false
      this.showCustomerInfo = false
      this.showSearchButton = false
      this.showBackButton = false
    },
    showSearchField() {
      this.showSearchButton = false
      this.resetSearchParams()
    },
    resetSearchParams() {
      this.searchParams.accountNumber = null
      this.searchParams.accountName = null
      this.searchParams.customerID = null
      this.searchParams.referenceNumber = null
    },
    resetModal() {
      this.customerList = []
    },
    backNavigationAction() {
      this.$router.push({
        name: 'outward-transfers',
      })
    },
    // Customer Details
    validateAccountDetails() {
      return new Promise((resolve, reject) => {
        this.$refs.accountDetailsRule.validate().then(success => {
          if (success) {
            if (this.outwardTransferFormData.amount !== '') {
              this.fetchFeeRange()
            }
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },

    validateFees() {
      return new Promise((resolve, reject) => {
        this.$refs.feesRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
  },
}
</script>
<style lang="scss">
//  .table-format {
//   tr td {
//     border: 0.5px solid black;
//     border-style: dotted;
//   }
// }
@import '@core/scss/vue/libs/vue-wizard.scss';
@import '@core/scss/vue/libs/vue-select.scss';
</style>
